// Helpers, constantes e subcomponentes de apresentacao extraidos de
// RFQDetailPage.js (ARCH-02). Codigo movido sem alteracoes de comportamento.
import { Sparkles } from 'lucide-react';
import { t } from '@/i18n/translations';
import { formatDate, formatEuro, formatNumber } from '@/lib/utils';
import { Input } from '@/components/ui/input';
import { Label, RequiredLabel } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

// --- Helpers -----------------------------------------------------------------

function extractRiskFromJustification(justification) {
  if (!justification) return null;
  const m = /(?:^|\s)risk=(low|medium|high)\b/i.exec(justification);
  return m ? m[1].toLowerCase() : null;
}

function extractReworkVersion(justification) {
  if (!justification) return null;
  const m = /^Retrabalho\s+v(\d+)/i.exec(justification);
  return m ? parseInt(m[1], 10) : null;
}

function extractFormVersion(justification) {
  if (!justification) return null;
  const m = /^v(\d+)\s+(selada|engenharia)/i.exec(justification);
  return m ? parseInt(m[1], 10) : null;
}

function isOverdue(rfq) {
  if (!rfq?.deadline) return false;
  if (!['in_analysis', 'awaiting_commercial_margins'].includes(rfq.status)) return false;
  return new Date(rfq.deadline).getTime() < Date.now();
}

/**
 * Peças/dia (sem sucata) no card VA Fundição.
 *
 * A rejeição está gravada como fracção 0–1 (PercentField mostra % e
 * escreve /100). A fórmula pedida é peças/dia × (1 − rejeição/100)
 * sobre o valor mostrado no ecrã — equivalente a peças/dia × (1 − scrap).
 */
function piecesPerDayWithoutScrap(piecesPerDay, scrapRate) {
  const scrap = Number(scrapRate);
  const rate = Number.isFinite(scrap) ? Math.min(Math.max(scrap, 0), 1) : 0;
  return Math.round((Number(piecesPerDay) || 0) * (1 - rate));
}

/**
 * Peças por caixa a partir do volume útil da caixa e da bounding box da peça.
 * Espelha ``_autocompute_pieces_per_box`` no backend: devolve ``null`` quando
 * falta a caixa ou alguma dimensão, para o caller preservar o valor actual.
 */
function computePiecesPerBox(box, pieceLengthMm, pieceWidthMm, pieceHeightMm) {
  if (!box) return null;
  const L = Number(pieceLengthMm);
  const W = Number(pieceWidthMm);
  const H = Number(pieceHeightMm);
  if (!(L > 0) || !(W > 0) || !(H > 0)) return null;
  const pieceVolumeMm3 = L * W * H;
  const volL = Number(box.volume_l);
  let boxVolumeMm3;
  if (Number.isFinite(volL) && volL > 0) {
    boxVolumeMm3 = volL * 1_000_000;
  } else {
    boxVolumeMm3 = (Number(box.length_mm) || 0)
      * (Number(box.width_mm) || 0)
      * (Number(box.height_mm) || 0);
  }
  if (!(boxVolumeMm3 > 0)) return null;
  return Math.max(Math.floor(boxVolumeMm3 / pieceVolumeMm3), 0);
}

function findBoxType(boxTypes, boxTypeId) {
  if (boxTypeId == null || boxTypeId === '' || boxTypeId === 'none') return null;
  const id = Number(boxTypeId);
  return (boxTypes || []).find((b) => Number(b.id) === id) || null;
}

function applyAutoPiecesPerBox(quotation, boxTypes) {
  if (!quotation) return quotation;
  const auto = computePiecesPerBox(
    findBoxType(boxTypes, quotation.box_type_id),
    quotation.piece_length_mm,
    quotation.piece_width_mm,
    quotation.piece_height_mm,
  );
  if (auto == null) return quotation;
  if (Number(quotation.pieces_per_box) === auto) return quotation;
  return { ...quotation, pieces_per_box: auto };
}

/** Europalete + tautliner TIR — envelope usado em Caixas por palete. */
const EUROPALLET_LENGTH_MM = 1200;
const EUROPALLET_WIDTH_MM = 800;
const EUROPALLET_HEIGHT_MM = 144;
const TIR_USEFUL_HEIGHT_MM = 2700;
const DEFAULT_MAX_PALLET_WEIGHT_KG = 1500;

function boxesPerLayerOnEuropallet(boxLengthMm, boxWidthMm) {
  const L = Number(boxLengthMm);
  const W = Number(boxWidthMm);
  if (!(L > 0) || !(W > 0)) return 0;
  const a = Math.floor(EUROPALLET_LENGTH_MM / L) * Math.floor(EUROPALLET_WIDTH_MM / W);
  const b = Math.floor(EUROPALLET_LENGTH_MM / W) * Math.floor(EUROPALLET_WIDTH_MM / L);
  return Math.max(a, b, 0);
}

/**
 * Caixas por palete: min(geometria na europalete até à altura útil do TIR,
 * peso máximo de peças). Espelha ``autocompute_boxes_per_pallet``.
 */
function computeBoxesPerPallet(box, piecesPerBox, pieceWeightKg, maxPalletWeightKg) {
  if (!box) return null;
  const boxH = Number(box.height_mm);
  if (!(boxH > 0)) return null;
  const perLayer = boxesPerLayerOnEuropallet(box.length_mm, box.width_mm);
  const layers = Math.floor((TIR_USEFUL_HEIGHT_MM - EUROPALLET_HEIGHT_MM) / boxH);
  const byHeight = perLayer * Math.max(layers, 0);
  const weightPerBox = (Number(piecesPerBox) || 0) * (Number(pieceWeightKg) || 0);
  const maxW = Number(maxPalletWeightKg) > 0
    ? Number(maxPalletWeightKg)
    : DEFAULT_MAX_PALLET_WEIGHT_KG;
  if (weightPerBox > 0) {
    return Math.min(byHeight, Math.floor(maxW / weightPerBox));
  }
  return byHeight;
}

function applyAutoBoxesPerPallet(quotation, boxTypes) {
  if (!quotation) return quotation;
  const auto = computeBoxesPerPallet(
    findBoxType(boxTypes, quotation.box_type_id),
    quotation.pieces_per_box,
    quotation.piece_weight_kg,
    DEFAULT_MAX_PALLET_WEIGHT_KG,
  );
  if (auto == null) return quotation;
  if (Number(quotation.boxes_per_pallet) === auto) return quotation;
  return { ...quotation, boxes_per_pallet: auto };
}

// ----- Form versioning helpers --------------------------------------------

// Fields from RFQ meta that participate in version diffs (mirrors backend).
const RFQ_SNAPSHOT_FIELDS = [
  'reference_number', 'client_id', 'part_reference', 'part_designation',
  'status', 'deadline', 'annual_volume', 'commercial_context',
  'strategic_notes', 'engineering_responsible',
  'commercial_result', 'result_notes', 'loss_reason',
];

// Quotation fields excluded from diff (mirror backend skip list + derived calcs).
const QUOTATION_SKIP = new Set([
  'id', 'rfq_id', 'created_at', 'updated_at', 'calc_snapshot',
  'calc_vm_eur_per_piece', 'calc_va_foundry_eur_per_piece',
  'calc_va_finishing_eur_per_piece', 'calc_subcontract_total_eur',
  'calc_packaging_eur_per_piece', 'calc_transport_eur_per_piece',
  'calc_production_cost_eur', 'calc_sale_price_eur',
  'calc_tooling_amortized_eur',
]);

/** Child tables stored at snapshot root (mirrors backend `_build_form_snapshot`). */
const QUOTATION_CHILD_COLLECTIONS = ['components', 'subcontract_candidates', 'tooling_lines', 'study_lines'];
const CHILD_ROW_SKIP = new Set(['id', 'quotation_id', '_key']);

/** Gera UUID estável para linhas editáveis (components / subcontratação). */
function newRowUid() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `uid_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
}

/** Reindexa ``position`` após add/remove/reorder. */
function normalisePositions(rows) {
  return (rows || []).map((row, index) => ({ ...row, position: index }));
}

const ROW_UID_COLLECTIONS = ['components', 'subcontract_candidates'];

/** Garante ``row_uid`` em cada linha das coleções editáveis (nunca por índice). */
function ensureStableRowUids(data) {
  if (!data) return data;
  const next = { ...data };
  for (const key of ROW_UID_COLLECTIONS) {
    if (!Array.isArray(next[key])) continue;
    next[key] = normalisePositions(
      next[key].map((row) => ({
        ...row,
        row_uid: row.row_uid || newRowUid(),
      })),
    );
  }
  return next;
}

function normalizeValue(v) {
  if (v === undefined || v === null) return null;
  // Dates may come back as ISO strings from backend and JS objects locally; normalize.
  if (v instanceof Date) return v.toISOString();
  return v;
}

function serializeQuotationChildRows(rows) {
  if (!Array.isArray(rows)) return [];
  return rows.map((row) => {
    const o = {};
    const keys = Object.keys(row || {}).filter((k) => !CHILD_ROW_SKIP.has(k)).sort();
    for (const k of keys) o[k] = normalizeValue(row[k]);
    return o;
  });
}

function buildCurrentSnapshot(rfq, quotation) {
  const rfqPart = {};
  for (const f of RFQ_SNAPSHOT_FIELDS) rfqPart[f] = normalizeValue(rfq?.[f]);
  const qPart = {};
  let components = [];
  let subcontract_candidates = [];
  let tooling_lines = [];
  let study_lines = [];
  if (quotation) {
    for (const k of Object.keys(quotation)) {
      if (QUOTATION_SKIP.has(k)) continue;
      if (QUOTATION_CHILD_COLLECTIONS.includes(k)) continue;
      qPart[k] = normalizeValue(quotation[k]);
    }
    components = serializeQuotationChildRows(quotation.components);
    subcontract_candidates = serializeQuotationChildRows(quotation.subcontract_candidates);
    tooling_lines = serializeQuotationChildRows(quotation.tooling_lines);
    study_lines = serializeQuotationChildRows(quotation.study_lines);
  }
  return {
    rfq: rfqPart,
    quotation: qPart,
    components,
    subcontract_candidates,
    tooling_lines,
    study_lines,
  };
}

function valuesEqual(a, b) {
  if (a === b) return true;
  if (a == null && b == null) return true;
  if (a == null || b == null) return false;
  if (typeof a === 'object' || typeof b === 'object') {
    try { return JSON.stringify(a) === JSON.stringify(b); } catch { return false; }
  }
  if (!isNaN(Number(a)) && !isNaN(Number(b))) return Number(a) === Number(b);
  return String(a) === String(b);
}

// Returns Set<string> of keys shaped "rfq.<field>" / "quotation.<field>" that differ
// between the baseline snapshot and the current form state.
function diffKeys(baselineSnapshot, currentRfq, currentQuotation) {
  const set = new Set();
  if (!baselineSnapshot) return set;
  const current = buildCurrentSnapshot(currentRfq, currentQuotation);
  for (const section of ['rfq', 'quotation']) {
    const base = (baselineSnapshot[section] || {});
    const cur = (current[section] || {});
    const keys = new Set([...Object.keys(base), ...Object.keys(cur)]);
    for (const k of keys) {
      if (!valuesEqual(base[k], cur[k])) set.add(`${section}.${k}`);
    }
  }
  for (const coll of QUOTATION_CHILD_COLLECTIONS) {
    const base = baselineSnapshot[coll] || [];
    const cur = current[coll] || [];
    if (!valuesEqual(base, cur)) set.add(coll);
  }
  return set;
}

function formatDiffValue(v) {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'boolean') return v ? 'sim' : 'não';
  if (typeof v === 'object') {
    try { return JSON.stringify(v); } catch { return String(v); }
  }
  return String(v);
}

// Fallback usado APENAS quando a API admin falha. A lista canónica vem do
// /config/finishing-rates (gerida pelo admin no AdminPage > "Acabamento").
const DEFAULT_FINISHING_OPS = [
  { name: 'Backstand/Rebarbagem', enabled: false, cadence_pcs_hr: 60, rate_eur_hr: 35, scrap_percent: 0.02 },
  { name: 'Granalagem', enabled: false, cadence_pcs_hr: 120, rate_eur_hr: 30, scrap_percent: 0.01 },
  { name: 'Polimento', enabled: false, cadence_pcs_hr: 40, rate_eur_hr: 35, scrap_percent: 0.02 },
  { name: 'Maquinagem CNC', enabled: false, cadence_pcs_hr: 30, rate_eur_hr: 55, scrap_percent: 0.03 },
  { name: 'Controlo/Gabarit', enabled: false, cadence_pcs_hr: 80, rate_eur_hr: 30, scrap_percent: 0.01 },
  { name: 'Pintura', enabled: false, cadence_pcs_hr: 50, rate_eur_hr: 45, scrap_percent: 0.02 },
];

// Converte uma lista de FinishingRateConfig (apenas activos) na estrutura
// usada pelos painéis "VA Acabamento" / "Trabalhos Extra" da cotação. A
// tarifa €/h é puxada da configuração e fica read-only para o comercial;
// cadência (peças/h) e rejeição (%) começam vazias — o comercial
// preenche-as por RFQ.
function buildFinishingOpsFromConfig(rates, category = 'finishing') {
  if (!Array.isArray(rates) || rates.length === 0) {
    return category === 'extra_works' ? [] : DEFAULT_FINISHING_OPS;
  }
  return rates
    .filter((r) => r.active && (r.category || 'finishing') === category)
    .map((r) => ({
      name: r.operation_name,
      enabled: false,
      cadence_pcs_hr: 0,
      rate_eur_hr: r.default_rate_eur_hr ?? 0,
      scrap_percent: 0,
    }));
}

// Legacy RFQs created before the rename may still carry the old label.
// Keep a map so the UI shows the canonical name without touching stored data.
const FINISHING_OP_ALIASES = {
  'Lixagem': 'Polimento',
};

function canonicalFinishingOp(name) {
  return FINISHING_OP_ALIASES[name] || name;
}

function finishingOpSlug(name) {
  return canonicalFinishingOp(name)
    .toLowerCase()
    .replace(/[\/\s]+/g, '_')
    .replace(/[^a-z0-9_]/g, '')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '');
}

/** Localized finishing operation label (falls back to canonical stored name). */
function finishingOpLabel(name, lang) {
  const key = `finishing_op_${finishingOpSlug(name)}`;
  const tr = t(key, lang);
  if (tr === key) return canonicalFinishingOp(name);
  return tr;
}

function formatPricingMarginPct(decimal) {
  const n = Number(decimal);
  if (Number.isNaN(n)) return '0';
  const s = (n * 100).toFixed(1);
  return s.endsWith('.0') ? s.slice(0, -2) : s;
}

function bucketVmLabel(lang, details) {
  const pct = formatPricingMarginPct(details?.buckets?.vm ?? 0);
  return `${t('bucket_vm', lang)} (+${pct}%)`;
}

function bucketVaPackTransLabel(lang, details) {
  const base = t('bucket_va_pack_trans', lang);
  const m = details?.margins;
  if (!m) return `${base} (+35%)`;
  const vi = formatPricingMarginPct(m.va_internal);
  const vp = formatPricingMarginPct(m.packaging);
  const vt = formatPricingMarginPct(m.transport);
  // Margem dos trabalhos extra é distinta de m.va_internal sempre que o
  // utilizador a parametrize; mostramo-la como segmento adicional só
  // nesse caso para não poluir o label quando ainda não foi tocada.
  if (m.extra_works != null && m.extra_works !== m.va_internal) {
    const ve = formatPricingMarginPct(m.extra_works);
    return `${base} (+${vi}% / +${ve}% / +${vp}% / +${vt}%)`;
  }
  return `${base} (+${vi}% / +${vp}% / +${vt}%)`;
}

// Labels individualizados — mostram a margem efectiva (idêntica à
// configurada em "Margens comerciais (segmentadas)") ao lado do nome.
// Assim o utilizador vê de forma 1-para-1 que margem foi aplicada a cada
// componente do PV final.
function bucketVaInternalLabel(lang, details) {
  const base = t('bucket_va_internal', lang);
  const pct = formatPricingMarginPct(details?.margins?.va_internal ?? 0);
  return `${base} (+${pct}%)`;
}

function bucketExtraWorksLabel(lang, details) {
  const base = t('bucket_extra_works', lang);
  const m = details?.margins;
  // Se não houver margem extra parametrizada, herda da interna.
  const eff = m?.extra_works != null ? m.extra_works : (m?.va_internal ?? 0);
  const pct = formatPricingMarginPct(eff);
  return `${base} (+${pct}%)`;
}

function bucketPackagingLabel(lang, details) {
  const base = t('bucket_packaging', lang);
  const pct = formatPricingMarginPct(details?.margins?.packaging ?? 0);
  return `${base} (+${pct}%)`;
}

function bucketTransportLabel(lang, details) {
  const base = t('bucket_transport', lang);
  const pct = formatPricingMarginPct(details?.margins?.transport ?? 0);
  return `${base} (+${pct}%)`;
}

function bucketComponentsLabel(lang, details) {
  const pct = formatPricingMarginPct(details?.buckets?.components ?? 0);
  return `${t('bucket_components', lang)} (+${pct}%)`;
}

function bucketSubcontractLabel(lang, details) {
  const pct = formatPricingMarginPct(details?.buckets?.subcontract ?? 0);
  return `${t('bucket_subcontract', lang)} (+${pct}%)`;
}

const DIFF_RING = 'ring-2 ring-amber-400 ring-offset-1 ring-offset-background';
// Indicador visual para campos pré-preenchidos a partir do desenho 2D.
// Mantém-se até o utilizador editar manualmente o campo (ver `updateField`).
const SUGGESTED_RING = 'ring-2 ring-emerald-400 ring-offset-1 ring-offset-background';

function SuggestedBadge({ lang }) {
  return (
    <span
      className="inline-flex items-center gap-1 rounded bg-emerald-500/15 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700 dark:text-emerald-300"
      title={t('suggest_field_tooltip', lang)}
    >
      <Sparkles className="h-2.5 w-2.5" />
      {t('suggest_field_badge', lang)}
    </span>
  );
}

function FieldLabel({ id, label, suggested, lang }) {
  return (
    <div className="flex items-center gap-2">
      <Label htmlFor={id}>{label}</Label>
      {suggested && <SuggestedBadge lang={lang} />}
    </div>
  );
}

function NumField({ id, label, value, onChange, step = 'any', disabled, min, highlight, suggested, lang = 'pt' }) {
  return (
    <div className="space-y-1.5">
      <FieldLabel id={id} label={label} suggested={suggested} lang={lang} />
      <Input
        id={id}
        type="number"
        step={step}
        min={min}
        value={value ?? ''}
        disabled={disabled}
        className={highlight ? DIFF_RING : (suggested ? SUGGESTED_RING : '')}
        onChange={(e) => onChange(e.target.value === '' ? '' : parseFloat(e.target.value))}
      />
    </div>
  );
}

function IntField({ id, label, value, onChange, disabled, min, highlight, suggested, lang = 'pt' }) {
  return (
    <div className="space-y-1.5">
      <FieldLabel id={id} label={label} suggested={suggested} lang={lang} />
      <Input
        id={id}
        type="number"
        step={1}
        min={min}
        value={value ?? ''}
        disabled={disabled}
        className={highlight ? DIFF_RING : (suggested ? SUGGESTED_RING : '')}
        onChange={(e) => onChange(e.target.value === '' ? '' : parseInt(e.target.value, 10))}
      />
    </div>
  );
}

// Renders a percentage stored as a decimal (0.2 -> "20") so the UI shows and
// accepts human-friendly percentage numbers while the backend keeps using the
// 0..1 range. Dividing by 100 on write keeps precision (e.g. 17.5 -> 0.175).
function PercentField({ id, label, value, onChange, step = 0.1, disabled, highlight, suggested, lang = 'pt' }) {
  const displayed = value == null || value === '' ? '' : Number((value * 100).toFixed(4));
  return (
    <div className="space-y-1.5">
      <FieldLabel id={id} label={label} suggested={suggested} lang={lang} />
      <Input
        id={id}
        type="number"
        step={step}
        value={displayed}
        disabled={disabled}
        className={highlight ? DIFF_RING : (suggested ? SUGGESTED_RING : '')}
        onChange={(e) => {
          const raw = e.target.value;
          if (raw === '') { onChange(0); return; }
          const num = parseFloat(raw);
          if (Number.isNaN(num)) return;
          onChange(num / 100);
        }}
      />
    </div>
  );
}

/**
 * Select de fornecedor para o card de Subcontratação. Filtra o catálogo
 * (admin → Fornecedores) pela ``category`` da linha, garantindo que só
 * aparecem fornecedores compatíveis (uma linha de Pintura só vê
 * fornecedores de Pintura).
 *
 * Suporta valores legados (texto livre persistido em RFQs antigas):
 * quando ``value`` não casa com nenhum fornecedor activo do catálogo,
 * cai-se para um <Input> livre — assim não perdemos dados históricos.
 */
function SupplierSelect({ value, category, suppliers, lang, disabled, onChange }) {
  const options = (suppliers || [])
    .filter((s) => s.active && s.supplier_type === category)
    .map((s) => s.name);
  const currentMatchesCatalog = !value || options.includes(value);
  if (!currentMatchesCatalog) {
    return (
      <Input
        value={value || ''}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  }
  if (options.length === 0) {
    return (
      <Input
        value={value || ''}
        placeholder={t('supplier_no_options', lang)}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  }
  return (
    <Select
      value={value || ''}
      disabled={disabled}
      onValueChange={(v) => onChange(v === '__none__' ? '' : v)}
    >
      <SelectTrigger>
        <SelectValue placeholder={t('supplier_select_placeholder', lang)} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="__none__">—</SelectItem>
        {options.map((name) => (
          <SelectItem key={name} value={name}>
            {name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function ReadOnlyNumField({ id, label, value }) {
  // Rendered as a static display instead of <input type="number"> so the native
  // spinner buttons can't increment the value — `readOnly` alone still lets the
  // arrows modify it in most browsers.
  const shown = value == null || value === '' ? '—' : value;
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      <div
        id={id}
        role="textbox"
        aria-readonly="true"
        className="flex h-9 w-full items-center rounded-md border border-input bg-muted/50 px-3 py-1 text-sm tabular-nums text-muted-foreground"
      >
        {shown}
      </div>
    </div>
  );
}

function EngineerAssignBlock({ lang, rfq, users, metaSaving, setRfq, saveRfqMeta }) {
  if (!rfq) return null;
  return (
    <div className="space-y-1.5">
      <RequiredLabel>{t('engineer', lang)}</RequiredLabel>
      <Select
        value={rfq.engineering_responsible ? String(rfq.engineering_responsible) : ''}
        onValueChange={(v) => {
          const engId = v ? parseInt(v, 10) : null;
          setRfq((prev) => ({ ...prev, engineering_responsible: engId }));
          saveRfqMeta({ engineering_responsible: engId });
        }}
        disabled={metaSaving}
      >
        <SelectTrigger>
          <SelectValue placeholder={t('select_placeholder', lang)} />
        </SelectTrigger>
        <SelectContent>
          {(users || [])
            .filter((u) => u.active)
            .map((u) => (
              <SelectItem key={u.id} value={String(u.id)}>
                {u.full_name}
              </SelectItem>
            ))}
        </SelectContent>
      </Select>
    </div>
  );
}

// ============ VERSION SNAPSHOT VIEWER ============
// Maps snapshot keys (produced by the backend `_build_form_snapshot`) to the
// i18n keys we already have, so the viewer reuses the same labels as the form.
// Mapeamento campo-código → chave de tradução para o diff de versões.
// IMPORTANTE: o backend (``_diff_snapshots``) emite TODOS os campos editáveis
// que mudaram entre versões. Sempre que se adiciona uma coluna nova à
// Quotation/RFQ que entre no snapshot é OBRIGATÓRIO mapeá-la aqui — caso
// contrário a tabela "Diferenças vs versão anterior" mostra o nome cru do
// campo (ex.: ``quotation.piece_length_mm``) em vez de um label legível.
const SNAPSHOT_LABEL_KEYS = {
  rfq: {
    reference_number: 'reference',
    client_id: 'client',
    part_reference: 'part_ref',
    part_designation: 'part_designation',
    status: 'status',
    deadline: 'deadline',
    annual_volume: 'annual_volume',
    commercial_context: 'commercial_context',
    strategic_notes: 'strategic_notes',
    engineering_responsible: 'engineer',
    commercial_result: 'commercial_result',
    result_notes: 'result_notes',
    loss_reason: 'loss_reason',
  },
  quotation: {
    // VM (matéria)
    calc_mode: 'calc_mode',
    alloy_code: 'alloy',
    base_mb_eur_per_t: 'base_mb',
    alloy_premium_eur_per_t: 'premium',
    paf_percent: 'paf',
    piece_weight_kg: 'piece_weight',
    runner_weight_kg: 'runner_weight',
    recycling_rate: 'recycling_rate',
    // Geometria + máquina
    piece_length_mm: 'piece_length_mm',
    piece_width_mm: 'piece_width_mm',
    piece_height_mm: 'piece_height_mm',
    machine_pressure_bar: 'machine_pressure_bar',
    projected_area_cm2: 'projected_area',
    projection_coef: 'projection_coef',
    safety_factor: 'safety_factor',
    mold_complexity_coef: 'mold_complexity_coef',
    runner_weight_coef: 'runner_weight_coef',
    machine_id: 'machine',
    machine_override: 'machine_override',
    num_cavities: 'cavities',
    shots_per_hour: 'shots_hour',
    trs_percent: 'trs',
    cycle_time_s: 'cycle_time',
    cycle_time_manual: 'cycle_time_manual',
    scrap_rate_foundry_percent: 'scrap_rate',
    // Subcontratação (legado de campos planos — hoje vivem em
    // ``subcontract_candidates``, mas o diff continua a mostrar quando
    // alteram via outras vias).
    subcontract_machining_eur: 'subcontract_machining',
    subcontract_machining_supplier: 'subcontract_machining_supplier',
    subcontract_surface_treatment_eur: 'subcontract_surface',
    subcontract_painting_eur: 'subcontract_painting',
    subcontract_other_eur: 'subcontract_other',
    subcontract_other_description: 'subcontract_other_description',
    // Embalagem
    pieces_per_box: 'pieces_per_box',
    boxes_per_pallet: 'boxes_per_pallet',
    boxes_per_lot: 'boxes_per_lot',
    packaging_separators_per_box: 'separators_per_box',
    box_type_id: 'box_type_label',
    packaging_cost_eur_per_piece: 'packaging_per_piece',
    pallet_cost_eur: 'pallet_cost',
    max_pallet_weight_kg: 'max_pallet_weight',
    // Transporte
    transport_incoterm: 'transport_incoterm',
    transport_km: 'transport_km',
    transport_eur_per_km: 'transport_eur_per_km',
    transport_pieces_per_trip: 'transport_pieces_per_trip',
    transport_cost_eur_per_lot: 'transport_per_lot',
    transport_pieces_per_lot: 'pieces_per_lot',
    // Margens
    margin_percent: 'margin_percent_label',
    margin_internal_ops_percent: 'margin_internal_ops',
    margin_packaging_percent: 'margin_packaging',
    margin_transport_percent: 'margin_transport',
    margin_extra_works_percent: 'margin_extra_works',
    margin_subcontract_percent: 'margin_subcontract',
    margin_components_percent: 'margin_components',
    // Tooling top-level (linhas detalhadas vivem em ``tooling_lines``)
    tooling_cost_foundry_eur: 'tooling_cost_foundry_eur',
    tooling_margin_percent: 'tooling_margin',
    tooling_sale_price_eur: 'tooling_sale_price_eur',
    gabarit_cost_eur: 'gabarit_cost_eur',
    gabarit_margin_percent: 'gabarit_margin_percent',
    // Operações inline (JSON columns) — quando mudam aparecem como
    // strings serializadas; renderizamos só um indicador genérico.
    finishing_operations: 'va_finishing_section',
    extra_works_operations: 'extra_works_section',
    inventory_shrink: 'inventory_shrink_label',
  },
};

// Etiquetas amigáveis para as 4 colecções (componentes, candidatos ST,
// tooling, estudos) e para as colunas internas das suas linhas. Usadas
// pelo formatter do diff de "__collection__".
const COLLECTION_TITLE_KEYS = {
  components: 'components_section',
  subcontract_candidates: 'subcontract_section',
  tooling_lines: 'tooling_section',
  study_lines: 'studies_section',
};

const COLLECTION_FIELD_KEYS = {
  components: {
    reference: 'reference',
    purchase_cost_eur: 'cost_eur',
    transport_eur: 'transport_per_lot',
    qty_per_piece: 'qty_per_piece',
    scrap_detected: 'scrap_rate',
    inventory_shrink: 'inventory_shrink_label',
    supplier_margin: 'margin_percent_label',
    position: 'position',
  },
  subcontract_candidates: {
    category: 'category',
    supplier: 'supplier',
    lot_size: 'lot_size',
    purchase_cost_eur: 'cost_eur',
    transport_per_pallet_eur: 'transport_per_lot',
    pcs_per_pallet: 'pcs_per_pallet',
    scrap_percent: 'scrap_rate',
    selected: 'selected',
    position: 'position',
  },
  tooling_lines: {
    label: 'designation',
    cost_eur: 'cost_eur',
    margin_percent: 'tooling_margin',
    guarantee_shots: 'tooling_guarantee_shots',
    position: 'position',
  },
  study_lines: {
    label: 'designation',
    cost_eur: 'cost_eur',
    margin_percent: 'tooling_margin',
    guarantee_shots: 'tooling_guarantee_shots',
    position: 'position',
  },
};

// Sections mirror the live quotation form (see the render block under
// `vm_section` / `va_foundry_section` / ...). Keep this in sync if the form
// groups change. The `section` property is the snapshot root ('rfq' or 'quotation').
const SNAPSHOT_SECTIONS = [
  {
    id: 'rfq_info',
    titleKey: 'rfq_info_title',
    fields: [
      { section: 'rfq', key: 'reference_number' },
      { section: 'rfq', key: 'status' },
      { section: 'rfq', key: 'client_id' },
      { section: 'rfq', key: 'part_reference' },
      { section: 'rfq', key: 'part_designation' },
      { section: 'rfq', key: 'annual_volume' },
      { section: 'rfq', key: 'deadline' },
      { section: 'rfq', key: 'engineering_responsible' },
      { section: 'rfq', key: 'commercial_context', wide: true },
      { section: 'rfq', key: 'strategic_notes', wide: true },
    ],
  },
  {
    id: 'material',
    titleKey: 'vm_section',
    fields: [
      { section: 'quotation', key: 'alloy_code' },
      { section: 'quotation', key: 'calc_mode' },
      { section: 'quotation', key: 'base_mb_eur_per_t' },
      { section: 'quotation', key: 'alloy_premium_eur_per_t' },
      { section: 'quotation', key: 'paf_percent' },
      { section: 'quotation', key: 'piece_weight_kg' },
      { section: 'quotation', key: 'runner_weight_kg' },
    ],
  },
  {
    id: 'foundry',
    titleKey: 'va_foundry_section',
    fields: [
      { section: 'quotation', key: 'piece_length_mm' },
      { section: 'quotation', key: 'piece_width_mm' },
      { section: 'quotation', key: 'piece_height_mm' },
      { section: 'quotation', key: 'machine_pressure_bar' },
      { section: 'quotation', key: 'projected_area_cm2' },
      { section: 'quotation', key: 'projection_coef' },
      { section: 'quotation', key: 'safety_factor' },
      { section: 'quotation', key: 'runner_weight_coef' },
      { section: 'quotation', key: 'machine_id' },
      { section: 'quotation', key: 'num_cavities' },
      { section: 'quotation', key: 'shots_per_hour' },
      { section: 'quotation', key: 'trs_percent' },
      { section: 'quotation', key: 'scrap_rate_foundry_percent' },
    ],
  },
  {
    id: 'finishing',
    titleKey: 'va_finishing_section',
    finOps: true,
  },
  {
    id: 'components',
    titleKey: 'components_section',
    collection: 'components',
  },
  {
    id: 'subcontract_candidates',
    titleKey: 'subcontract_section',
    collection: 'subcontract_candidates',
  },
  {
    id: 'packaging',
    titleKey: 'packaging_transport_section',
    fields: [
      { section: 'quotation', key: 'pieces_per_box' },
      { section: 'quotation', key: 'boxes_per_pallet' },
      { section: 'quotation', key: 'packaging_separators_per_box' },
      { section: 'quotation', key: 'box_type_id' },
      { section: 'quotation', key: 'pallet_cost_eur' },
      { section: 'quotation', key: 'max_pallet_weight_kg' },
      { section: 'quotation', key: 'transport_cost_eur_per_lot' },
      { section: 'quotation', key: 'transport_pieces_per_lot' },
      { section: 'quotation', key: 'transport_incoterm' },
      { section: 'quotation', key: 'transport_km' },
      { section: 'quotation', key: 'transport_eur_per_km' },
      { section: 'quotation', key: 'transport_pieces_per_trip' },
    ],
  },
  {
    id: 'tooling_lines',
    titleKey: 'tooling_section',
    collection: 'tooling_lines',
  },
  {
    id: 'study_lines',
    titleKey: 'studies_section',
    collection: 'study_lines',
  },
  {
    id: 'shrink',
    titleKey: 'shrink_section',
    fields: [
      { section: 'quotation', key: 'inventory_shrink' },
    ],
  },
  {
    id: 'outcome',
    titleKey: 'commercial_outcome_title',
    fields: [
      { section: 'rfq', key: 'commercial_result' },
      { section: 'rfq', key: 'loss_reason' },
      { section: 'rfq', key: 'result_notes', wide: true },
    ],
    onlyIfAny: true,
  },
];

// Fields formatted as decimals-as-percents (e.g. 0.2 → 20%).
const SNAPSHOT_PERCENT_KEYS = new Set([
  'paf_percent', 'trs_percent', 'scrap_rate_foundry_percent', 'margin_percent',
  'inventory_shrink',
]);

// Fields that are raw numeric amounts in euros (no multiplication).
const SNAPSHOT_EURO_KEYS = new Set([
  'base_mb_eur_per_t', 'alloy_premium_eur_per_t',
  'subcontract_machining_eur', 'subcontract_surface_treatment_eur',
  'subcontract_painting_eur', 'subcontract_other_eur',
  'packaging_cost_eur_per_piece', 'transport_cost_eur_per_lot',
  'pallet_cost_eur',
]);

// Fields that are free-form text / long descriptions.
const SNAPSHOT_TEXT_KEYS = new Set([
  'commercial_context', 'strategic_notes', 'result_notes',
]);

function formatSnapshotValue(key, val, lang, ctx = {}) {
  if (val === null || val === undefined || val === '') return '—';
  if (typeof val === 'boolean') return val ? t('yes', lang) : t('no', lang);

  // Resolved references — delegate to context maps loaded by the parent page.
  if (key === 'client_id') {
    const cid = val === '' || val == null ? null : Number(val);
    const name =
      (cid != null && Number.isFinite(cid) ? ctx.clientsById?.[cid] : null) ||
      (ctx.rfq?.client_id === cid ? ctx.rfq?.client?.name : null);
    return name || `#${val}`;
  }
  if (key === 'machine_id') {
    const mid = val === '' || val == null ? null : Number(val);
    const m = mid != null && Number.isFinite(mid) ? ctx.machinesById?.[mid] : null;
    return m ? `${m.name}${m.tonnage ? ` — ${m.tonnage}T` : ''}` : `#${val}`;
  }
  if (key === 'engineering_responsible') {
    const uid = val === '' || val == null ? null : Number(val);
    if (uid == null || !Number.isFinite(uid)) return val === null || val === '' ? '—' : String(val);
    const u = ctx.usersById?.[uid];
    return u?.full_name || u?.username || `#${uid}`;
  }
  if (key === 'box_type_id') {
    const bid = val === '' || val == null ? null : Number(val);
    if (bid == null || !Number.isFinite(bid)) return '—';
    const b = ctx.boxesById?.[bid];
    return b
      ? `${b.name} — ${b.length_mm}×${b.width_mm}×${b.height_mm} mm`
      : `#${bid}`;
  }
  if (key === 'transport_incoterm') {
    const v = String(val || '').toLowerCase();
    if (v === 'ddp') return 'DDP';
    if (v === 'dap') return 'DAP';
    if (v === 'exwork') return 'Ex Works';
    return t('transport_incoterm_other', lang);
  }

  // Known enum-like fields get translated (fallback to raw value).
  if (key === 'status') return t(String(val), lang) || String(val);
  if (key === 'calc_mode') {
    if (val === 'legacy_excel_mode') return t('legacy_mode', lang);
    if (val === 'enhanced_cost_mode') return t('enhanced_mode', lang);
    return String(val);
  }
  if (key === 'loss_reason') {
    const tkey = `loss_reason_${val}`;
    const translated = t(tkey, lang);
    return translated && translated !== tkey ? translated : String(val);
  }
  if (key === 'commercial_result') {
    const tkey = String(val);
    const translated = t(tkey, lang);
    return translated && translated !== tkey ? translated : String(val);
  }

  if (SNAPSHOT_PERCENT_KEYS.has(key) || key.endsWith('_percent')) {
    const n = Number(val);
    if (Number.isFinite(n)) return `${(n * 100).toFixed(1)}%`;
  }
  if (SNAPSHOT_EURO_KEYS.has(key) || (key.endsWith('_eur') || key.includes('_eur_'))) {
    const n = Number(val);
    if (Number.isFinite(n)) return formatEuro(n, lang);
  }
  if (key === 'deadline' || key.endsWith('_at') || key.endsWith('_date')) {
    try { return formatDate(val, lang); } catch { return String(val); }
  }
  if (key === 'annual_volume' || key === 'num_cavities' || key === 'transport_pieces_per_lot' || key === 'shots_per_hour') {
    const n = Number(val);
    if (Number.isFinite(n)) return formatNumber(n, lang, 0);
  }
  if (key === 'piece_weight_kg' || key === 'runner_weight_kg') {
    const n = Number(val);
    if (Number.isFinite(n)) return `${formatNumber(n, lang, 3)} kg`;
  }
  if (key === 'projected_area_cm2') {
    const n = Number(val);
    if (Number.isFinite(n)) return `${formatNumber(n, lang, 1)} cm²`;
  }

  if (Array.isArray(val)) return String(val.length);
  if (typeof val === 'object') {
    try { return JSON.stringify(val); } catch { return String(val); }
  }
  return String(val);
}

// Styled to look like the actual form fields (Label on top, value box below),
// so the version view feels like a read-only mirror of what the user edits.
function SnapshotField({ label, value, highlighted, wide, isText }) {
  return (
    <div className={`space-y-1.5 ${wide ? 'sm:col-span-2 md:col-span-3' : ''}`}>
      <Label className="text-xs text-muted-foreground">{label}</Label>
      <div
        className={`flex min-h-9 w-full items-center rounded-md border px-3 py-1.5 text-sm tabular-nums ${
          highlighted
            ? 'border-amber-400 bg-amber-50 font-medium text-foreground dark:bg-amber-950/30'
            : 'border-input bg-muted/40 text-foreground'
        } ${isText ? 'whitespace-pre-wrap break-words tabular-nums-none' : ''}`}
      >
        {value}
      </div>
    </div>
  );
}

function VersionSnapshotView({ snapshot, changes, lang, context }) {
  if (!snapshot) return null;
  const changedSet = new Set((changes || []).map((c) => `${c.section}.${c.field}`));

  return (
    <div className="space-y-5">
      {SNAPSHOT_SECTIONS.map((sec) => {
        // Calc_v2 child collections: render as a table mirroring the live form.
        if (sec.collection) {
          const items = snapshot.quotation?.[sec.collection];
          if (!Array.isArray(items) || items.length === 0) return null;
          return (
            <section key={sec.id} className="space-y-2">
              <h3 className="text-sm font-semibold text-foreground">{t(sec.titleKey, lang)}</h3>
              <div className="overflow-x-auto rounded-md border border-border">
                {sec.collection === 'components' && (
                  <table className="w-full text-xs">
                    <thead className="bg-muted/40 text-muted-foreground">
                      <tr>
                        <th className="px-3 py-2 text-left font-medium">{t('component_reference', lang)}</th>
                        <th className="px-3 py-2 text-right font-medium">{t('component_purchase', lang)}</th>
                        <th className="px-3 py-2 text-right font-medium">{t('component_transport', lang)}</th>
                        <th className="px-3 py-2 text-right font-medium">{t('component_qty', lang)}</th>
                        <th className="px-3 py-2 text-right font-medium">{t('component_scrap', lang)}</th>
                        <th className="px-3 py-2 text-right font-medium">{t('component_shrink', lang)}</th>
                        <th className="px-3 py-2 text-right font-medium">{t('component_supplier_margin', lang)}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((c, i) => (
                        <tr key={i} className="border-t border-border/60">
                          <td className="px-3 py-2 font-medium">{c.reference || '—'}</td>
                          <td className="px-3 py-2 text-right tnum">{formatEuro(c.purchase_cost_eur || 0, lang)}</td>
                          <td className="px-3 py-2 text-right tnum">{formatEuro(c.transport_eur || 0, lang)}</td>
                          <td className="px-3 py-2 text-right tnum">{c.qty_per_piece || 0}</td>
                          <td className="px-3 py-2 text-right tnum">{((c.scrap_detected || 0) * 100).toFixed(1)}%</td>
                          <td className="px-3 py-2 text-right tnum">{((c.inventory_shrink || 0) * 100).toFixed(1)}%</td>
                          <td className="px-3 py-2 text-right tnum">{((c.supplier_margin || 0) * 100).toFixed(1)}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
                {sec.collection === 'subcontract_candidates' && (
                  <table className="w-full text-xs">
                    <thead className="bg-muted/40 text-muted-foreground">
                      <tr>
                        <th className="px-3 py-2 text-left font-medium">{t('category', lang) || 'Category'}</th>
                        <th className="px-3 py-2 text-center font-medium">{t('selected', lang)}</th>
                        <th className="px-3 py-2 text-left font-medium">{t('supplier', lang)}</th>
                        <th className="px-3 py-2 text-right font-medium">{t('purchase', lang)}</th>
                        <th className="px-3 py-2 text-right font-medium">{t('transport_pallet', lang)}</th>
                        <th className="px-3 py-2 text-right font-medium">{t('pcs_pallet', lang)}</th>
                        <th className="px-3 py-2 text-right font-medium">{t('scrap_percent', lang)}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((c, i) => (
                        <tr key={i} className="border-t border-border/60">
                          <td className="px-3 py-2 font-medium">{t(`subcontract_${c.category}`, lang) || c.category}</td>
                          <td className="px-3 py-2 text-center">{c.selected ? '✓' : '—'}</td>
                          <td className="px-3 py-2">{c.supplier || '—'}</td>
                          <td className="px-3 py-2 text-right tnum">{formatEuro(c.purchase_cost_eur || 0, lang)}</td>
                          <td className="px-3 py-2 text-right tnum">{formatEuro(c.transport_per_pallet_eur || 0, lang)}</td>
                          <td className="px-3 py-2 text-right tnum">{c.pcs_per_pallet || 0}</td>
                          <td className="px-3 py-2 text-right tnum">{((c.scrap_percent || 0) * 100).toFixed(1)}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
                {(sec.collection === 'tooling_lines' || sec.collection === 'study_lines') && (
                  <table className="w-full text-xs">
                    <thead className="bg-muted/40 text-muted-foreground">
                      <tr>
                        <th className="px-3 py-2 text-left font-medium">{t('tooling_line_label', lang)}</th>
                        <th className="px-3 py-2 text-right font-medium">{t('tooling_cost', lang)}</th>
                        <th className="px-3 py-2 text-right font-medium">{t('tooling_margin', lang)}</th>
                        <th className="px-3 py-2 text-right font-medium">{t('tooling_guarantee_shots', lang)}</th>
                        <th className="px-3 py-2 text-right font-medium">{t('tooling_sale', lang)}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((l, i) => (
                        <tr key={i} className="border-t border-border/60">
                          <td className="px-3 py-2 font-medium">{t(`tooling_label_${l.label}`, lang) || l.label || '—'}</td>
                          <td className="px-3 py-2 text-right tnum">{formatEuro(l.cost_eur || 0, lang)}</td>
                          <td className="px-3 py-2 text-right tnum">{((l.margin_percent || 0) * 100).toFixed(1)}%</td>
                          <td className="px-3 py-2 text-right tnum">{l.guarantee_shots ?? '—'}</td>
                          <td className="px-3 py-2 text-right tnum">{formatEuro((l.cost_eur || 0) * (1 + (l.margin_percent || 0)), lang)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </section>
          );
        }

        // Finishing operations: render as a table mirroring the form header.
        if (sec.finOps) {
          const finOps = snapshot.quotation?.finishing_operations;
          if (!Array.isArray(finOps) || finOps.length === 0) return null;
          return (
            <section key={sec.id} className="space-y-2">
              <h3 className="text-sm font-semibold text-foreground">{t(sec.titleKey, lang)}</h3>
              <div className="overflow-x-auto rounded-md border border-border">
                <table className="w-full text-xs">
                  <thead className="bg-muted/40 text-muted-foreground">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium">{t('finishing_op_name', lang)}</th>
                      <th className="px-3 py-2 text-center font-medium">{t('active', lang)}</th>
                      <th className="px-3 py-2 text-right font-medium">{t('finishing_cadence_hdr', lang)}</th>
                      <th className="px-3 py-2 text-right font-medium">{t('finishing_rate_hdr', lang)}</th>
                      <th className="px-3 py-2 text-right font-medium">{t('finishing_scrap_hdr', lang)}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {finOps.map((op, i) => (
                      <tr key={i} className="border-t border-border/60">
                        <td className="px-3 py-2 font-medium">{finishingOpLabel(op.name, lang)}</td>
                        <td className="px-3 py-2 text-center">{op.enabled ? '✓' : '—'}</td>
                        <td className="px-3 py-2 text-right tnum">{op.cadence_pcs_hr || '—'}</td>
                        <td className="px-3 py-2 text-right tnum">
                          {op.rate_eur_hr ? formatEuro(op.rate_eur_hr, lang) : '—'}
                        </td>
                        <td className="px-3 py-2 text-right tnum">
                          {((op.scrap_percent || 0) * 100).toFixed(1)}%
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          );
        }

        // Collect the fields that actually exist in the snapshot.
        const renderable = (sec.fields || [])
          .map((f) => {
            const raw = snapshot[f.section]?.[f.key];
            return { ...f, raw };
          })
          .filter((f) => !(sec.onlyIfAny && (f.raw === null || f.raw === undefined || f.raw === '')));

        if (renderable.length === 0) return null;

        return (
          <section key={sec.id} className="space-y-3">
            <h3 className="text-sm font-semibold text-foreground">{t(sec.titleKey, lang)}</h3>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3">
              {renderable.map((f) => {
                const tkey = SNAPSHOT_LABEL_KEYS[f.section]?.[f.key];
                const label = tkey ? t(tkey, lang) : f.key;
                const highlighted = changedSet.has(`${f.section}.${f.key}`);
                const value = formatSnapshotValue(f.key, f.raw, lang, context);
                return (
                  <SnapshotField
                    key={`${f.section}.${f.key}`}
                    label={label}
                    value={value}
                    highlighted={highlighted}
                    wide={Boolean(f.wide)}
                    isText={SNAPSHOT_TEXT_KEYS.has(f.key)}
                  />
                );
              })}
            </div>
          </section>
        );
      })}
    </div>
  );
}

function g2Text(v) {
  return v == null ? '' : String(v);
}

function g2Day(iso) {
  return iso ? String(iso).substring(0, 10) : '';
}

/** Converte o valor de `<input type="date">` (`yyyy-mm-dd`) para ISO, como a UI G2. */
function g2DateInputToIso(yyyyMmDd) {
  return yyyyMmDd ? new Date(yyyyMmDd).toISOString() : null;
}

function civilDayInTimeZone(iso, timeZone) {
  if (!iso) return '';
  return new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date(iso));
}

/** true se o blur de um campo G2 vai para o botão Submeter (o save fica no flush). */
function g2BlurGoesToSubmit(relatedTarget) {
  return Boolean(
    relatedTarget &&
      typeof relatedTarget.closest === 'function' &&
      relatedTarget.closest('#g2-submit-engineering'),
  );
}

/**
 * Diff dos campos meta G2 (projecto / plano / prazo) vs o último RFQ
 * persistido. Usado no onBlur e no flush antes de submeter à Engenharia —
 * comparar com o estado React local saltava o PUT depois do onChange.
 */
function buildG2MetaPatch(local, persisted) {
  const patch = {};
  const src = local || {};
  const snap = persisted || {};
  if (g2Text(src.project_name) !== g2Text(snap.project_name)) {
    patch.project_name = src.project_name || '';
  }
  if (g2Text(src.plan_number) !== g2Text(snap.plan_number)) {
    patch.plan_number = src.plan_number || '';
  }
  if (g2Text(src.plan_revision) !== g2Text(snap.plan_revision)) {
    patch.plan_revision = src.plan_revision || '';
  }
  if (g2Day(src.plan_revision_date) !== g2Day(snap.plan_revision_date)) {
    patch.plan_revision_date = src.plan_revision_date || null;
  }
  if (g2Day(src.deadline) !== g2Day(snap.deadline)) {
    patch.deadline = src.deadline || null;
  }
  return patch;
}

function getSubmitReasons({ rfq, documents, awaitingClient, t, lang }) {
  const reasons = [];
  if (!rfq) return reasons;
  if (!documents || documents.length === 0) {
    reasons.push(t('missing_document', lang));
  }
  if (!rfq.deadline) reasons.push(t('missing_deadline', lang));
  if (awaitingClient) reasons.push(t('missing_client_reply', lang));
  return reasons;
}

function canSubmitEngineering(args) {
  return getSubmitReasons(args).length === 0;
}

function canUnlockEdit({ rfq, isEngineerResponsible }) {
  return Boolean(
    rfq && rfq.status === 'in_analysis' && !rfq.edit_mode && isEngineerResponsible,
  );
}

function canLockEdit({ rfq, isEngineerResponsible }) {
  return Boolean(
    rfq && rfq.status === 'in_analysis' && rfq.edit_mode && isEngineerResponsible,
  );
}

function canSubmitToCommercialMargins({ rfq, isEngineerUser }) {
  return Boolean(
    rfq && isEngineerUser && rfq.status === 'in_analysis' && !rfq.edit_mode,
  );
}

export {
  extractRiskFromJustification,
  extractReworkVersion,
  extractFormVersion,
  isOverdue,
  piecesPerDayWithoutScrap,
  g2Text,
  g2Day,
  g2DateInputToIso,
  civilDayInTimeZone,
  g2BlurGoesToSubmit,
  buildG2MetaPatch,
  getSubmitReasons,
  canSubmitEngineering,
  canUnlockEdit,
  canLockEdit,
  canSubmitToCommercialMargins,
  computePiecesPerBox,
  findBoxType,
  applyAutoPiecesPerBox,
  computeBoxesPerPallet,
  applyAutoBoxesPerPallet,
  RFQ_SNAPSHOT_FIELDS,
  QUOTATION_SKIP,
  QUOTATION_CHILD_COLLECTIONS,
  CHILD_ROW_SKIP,
  normalizeValue,
  serializeQuotationChildRows,
  buildCurrentSnapshot,
  valuesEqual,
  diffKeys,
  formatDiffValue,
  DEFAULT_FINISHING_OPS,
  buildFinishingOpsFromConfig,
  FINISHING_OP_ALIASES,
  canonicalFinishingOp,
  finishingOpSlug,
  finishingOpLabel,
  formatPricingMarginPct,
  bucketVmLabel,
  bucketVaPackTransLabel,
  bucketVaInternalLabel,
  bucketExtraWorksLabel,
  newRowUid,
  normalisePositions,
  ensureStableRowUids,
  bucketPackagingLabel,
  bucketTransportLabel,
  bucketComponentsLabel,
  bucketSubcontractLabel,
  DIFF_RING,
  SUGGESTED_RING,
  SuggestedBadge,
  FieldLabel,
  NumField,
  IntField,
  PercentField,
  SupplierSelect,
  ReadOnlyNumField,
  EngineerAssignBlock,
  SNAPSHOT_LABEL_KEYS,
  COLLECTION_TITLE_KEYS,
  COLLECTION_FIELD_KEYS,
  SNAPSHOT_SECTIONS,
  SNAPSHOT_PERCENT_KEYS,
  SNAPSHOT_EURO_KEYS,
  SNAPSHOT_TEXT_KEYS,
  formatSnapshotValue,
  SnapshotField,
  VersionSnapshotView,
};
